import React, { useState, useEffect } from 'react';
import { AlertCircle, Mail, Check, RefreshCw, X } from 'lucide-react';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const EmailVerificationBanner = () => {
  const { user, token, fetchUser } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (user && token) {
      checkVerificationStatus();
    }
  }, [user, token]);

  const checkVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/verification-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Failed to check verification status:', error);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      const response = await axios.post(`${API}/auth/resend-verification`, 
        { email: user.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.mode === 'preview' && response.data.verification_token) {
        // Preview mode - update status with new token
        setVerificationStatus(prev => ({
          ...prev,
          verification_token: response.data.verification_token
        }));
        toast.success('New verification link generated');
      } else {
        toast.success('Verification email sent!');
      }
    } catch (error) {
      toast.error('Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  const handleQuickVerify = async () => {
    if (!verificationStatus?.verification_token) return;
    
    setVerifying(true);
    try {
      await axios.post(`${API}/auth/verify-email`, {
        token: verificationStatus.verification_token
      });
      toast.success('Email verified successfully!');
      await fetchUser();
      await checkVerificationStatus();
    } catch (error) {
      toast.error('Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  // Don't show if user is verified or status not loaded yet
  if (!verificationStatus || verificationStatus.email_verified || dismissed) {
    return null;
  }

  const isPreviewMode = verificationStatus.is_preview_mode;

  return (
    <Alert className="mb-4 border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
      <AlertCircle className="h-4 w-4 text-amber-600" />
      <AlertDescription className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 w-full">
        <div className="flex-1">
          <span className="font-medium text-amber-800 dark:text-amber-200">
            Please verify your email to unlock full access.
          </span>
          <span className="text-amber-700 dark:text-amber-300 text-sm ml-2">
            Some features are restricted until verification.
          </span>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          {isPreviewMode && verificationStatus.verification_token ? (
            // Preview mode: Show quick verify button
            <Button
              size="sm"
              onClick={handleQuickVerify}
              disabled={verifying}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
              data-testid="quick-verify-btn"
            >
              {verifying ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Check className="w-3.5 h-3.5" />
              )}
              Verify Now (Preview)
            </Button>
          ) : (
            // Production mode: Show resend button
            <Button
              size="sm"
              variant="outline"
              onClick={handleResendVerification}
              disabled={resending}
              className="border-amber-300 text-amber-700 hover:bg-amber-100 gap-1"
              data-testid="resend-verification-btn"
            >
              {resending ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Mail className="w-3.5 h-3.5" />
              )}
              Resend Email
            </Button>
          )}
          
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDismissed(true)}
            className="text-amber-600 hover:text-amber-800 hover:bg-amber-100 p-1.5"
            data-testid="dismiss-verification-banner"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default EmailVerificationBanner;
